import React from 'react';

import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { combinedStudies } from './combinedStudies.js';
import { fileRevision, sameFileRevision, sameQrsRevision } from './fileRevision.js';
import FolderPreset from './FolderPreset.jsx';
import { alignedGraphMetadata, displayedRowStats, initialHrBounds } from './hrBounds.js';
import { reportScene, scenePdf, validateMetadata } from './hrRender.js';
import { parseRep } from './parseRep.js';
import PdfReportInsert from './PdfReportInsert.jsx';
import { filterStudies, openStudyFiles, scanStudyDirectory } from './studyFiles.js';
import { mergeStudyIds, studyLabel } from './studyMetadata.js';
import useFolderPreset from './useFolderPreset.js';

const emptyMeta = { facility: '', patient: '', study: '', gender: '', age: '', start: '' };
export default function HRReport() {
  const folderCache = useFolderPreset('studies');
  const secondaryCache = useFolderPreset('studies-secondary');
  const [secondaryEnabled, setSecondaryEnabled] = React.useState(true);
  const [secondaryDirectory, setSecondaryDirectory] = React.useState(null);
  const [secondaryStudies, setSecondaryStudies] = React.useState([]);
  const worker = React.useRef(null);
  const lastBuild = React.useRef(null);
  const directoryRef = React.useRef(null);
  const [studyDirectory, setStudyDirectory] = React.useState(null);
  const discovery = React.useRef(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const operation = React.useRef(0);
  const active = React.useRef(false);
  const [inserting, setInserting] = React.useState(false);
  const [files, setFiles] = React.useState(null);
  const [primaryStudies, setStudies] = React.useState([]);
  const studies = React.useMemo(
    () => combinedStudies(primaryStudies, secondaryEnabled ? secondaryStudies : []),
    [primaryStudies, secondaryStudies, secondaryEnabled]
  );
  const [selected, setSelected] = React.useState(null);
  const [folderName, setFolderName] = React.useState('');
  const [model, setModel] = React.useState(null);
  const [hrBytes, setHrBytes] = React.useState(null);
  const [url, setUrl] = React.useState('');
  const [phase, setPhase] = React.useState('');
  const [error, setError] = React.useState('');
  const [warnings, setWarnings] = React.useState([]);
  const [folderSupported, setFolderSupported] = React.useState(false);
  const [rate, setRate] = React.useState(128);
  const [repair, setRepair] = React.useState(true);
  const [meta, setMeta] = React.useState(emptyMeta);
  const busy = folderCache.restoring || secondaryCache.restoring || Boolean(phase) || inserting;
  React.useEffect(() => {
    if (!secondaryCache.preset) return;
    setSecondaryDirectory(secondaryCache.preset.directory);
    setSecondaryStudies(secondaryCache.preset.entries);
  }, [secondaryCache.preset]);
  React.useEffect(() => {
    if (!folderCache.preset) return;
    directoryRef.current = folderCache.preset.directory;
    setStudyDirectory(folderCache.preset.directory);
    setStudies(folderCache.preset.entries);
    setFolderName(folderCache.preset.directory.name);
  }, [folderCache.preset]);
  React.useEffect(() => {
    setFolderSupported(typeof window.showDirectoryPicker === 'function');
    return () => {
      operation.current += 1;
      worker.current?.terminate();
      active.current = false;
    };
  }, []);
  React.useEffect(() => {
    if (!model) {
      setUrl('');
      setHrBytes(null);
      return undefined;
    }
    let next;
    try {
      const bytes = scenePdf(reportScene(model, meta));
      setHrBytes(bytes);
      next = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      setUrl(next);
      setError('');
    } catch (err) {
      setUrl('');
      setHrBytes(null);
      setError(err.message);
    }
    return () => {
      if (next) URL.revokeObjectURL(next);
    };
  }, [model, meta]);
  const finish = (token) => {
    if (token !== operation.current) return;
    active.current = false;
    setPhase('');
  };
  const cancel = () => {
    operation.current += 1;
    worker.current?.terminate();
    worker.current = null;
    active.current = false;
    setPhase('');
  };
  const startWorker = (chosen, token) => {
    setPhase('Building HR report…');
    const next =
      worker.current || new Worker(new URL('./hrWorker.js', import.meta.url), { type: 'module' });
    worker.current = next;
    const handleMessage = ({ data }) => {
      removeListeners();
      if (token !== operation.current) return;
      finish(token);
      if (data.error) setError(data.error);
      else {
        lastBuild.current = { files: chosen, rate, repair, model: data.model };
        setMeta((current) => ({
          ...current,
          stitchRowStats: undefined,
          stitchLimits: undefined,
          ...initialHrBounds(data.model.rowStats),
        }));
        setModel(data.model);
      }
    };
    const handleError = () => {
      removeListeners();
      next.terminate();
      if (token !== operation.current) return;
      worker.current = null;
      finish(token);
      setError(
        'Unable to run the QRS worker. Check your browser and application worker configuration.'
      );
    };
    const removeListeners = () => {
      next.removeEventListener('message', handleMessage);
      next.removeEventListener('error', handleError);
    };
    next.addEventListener('message', handleMessage);
    next.addEventListener('error', handleError);
    next.postMessage({
      studyKey: chosen.id,
      files: [chosen.main, chosen.a, chosen.b],
      sampleRate: rate,
      repair,
    });
  };
  const selectStudy = (chosen) => {
    lastBuild.current = null;
    setSelected(chosen);
    setFiles(null);
    setModel(null);
    setWarnings([]);
    setError('');
    setMeta({ ...emptyMeta, study: chosen?.studyId || '' });
  };
  const chooseFolder = async () => {
    if (active.current) return;
    active.current = true;
    const token = ++operation.current;
    setPhase('Choosing folder…');
    setError('');
    try {
      // Invoke from the button's click gesture. Request read-only access, never write access.
      const directory = await window.showDirectoryPicker({ mode: 'read' });
      if (
        secondaryEnabled &&
        secondaryDirectory &&
        (await directory.isSameEntry(secondaryDirectory))
      )
        throw new Error('Choose a primary folder different from the secondary folder.');
      if (token !== operation.current) return;
      setPhase('Reading folder structure and REP study IDs…');
      const discovered = await scanStudyDirectory(directory, () => token !== operation.current);
      const found = await mergeStudyIds(discovered, [], () => token !== operation.current);
      if (token !== operation.current) return;
      directoryRef.current = directory;
      setStudyDirectory(directory);
      setStudies(found);
      folderCache.save(directory, found, '');
      setFolderName(directory.name);
      selectStudy(null);
      if (!found.length)
        setError('No REP/QRS study filenames were found in this folder or its subfolders.');
    } catch (err) {
      if (token === operation.current && err.name !== 'AbortError') setError(err.message);
    } finally {
      finish(token);
    }
  };
  const handleChooseSecondaryFolder = async () => {
    if (active.current) return;
    active.current = true;
    const token = ++operation.current;
    setPhase('Choosing secondary study folder…');
    setError('');
    try {
      const directory = await window.showDirectoryPicker({ mode: 'read' });
      if (studyDirectory && (await directory.isSameEntry(studyDirectory)))
        throw new Error('Choose a different folder for secondary studies.');
      const discovered = await scanStudyDirectory(directory, () => token !== operation.current);
      const found = await mergeStudyIds(discovered, [], () => token !== operation.current);
      if (token !== operation.current) return;
      setSecondaryDirectory(directory);
      setSecondaryStudies(found);
      setSecondaryEnabled(true);
      secondaryCache.save(directory, found, '');
      selectStudy(null);
    } catch (err) {
      if (token === operation.current && err.name !== 'AbortError') setError(err.message);
    } finally {
      finish(token);
    }
  };
  const handleSecondaryEnabledChange = (event) => {
    setSecondaryEnabled(event.target.checked);
    selectStudy(null);
  };
  const handleLiveStudiesUpdate = (_found, folders) => {
    setStudies(folders.primary);
    folderCache.save(studyDirectory, folders.primary);
    if (secondaryEnabled && secondaryDirectory) {
      setSecondaryStudies(folders.secondary);
      secondaryCache.save(secondaryDirectory, folders.secondary);
    }
  };
  const refreshStudies = async () => {
    if (!folderCache.allowed || !directoryRef.current || discovery.current) return;
    discovery.current = true;
    setRefreshing(true);
    const directory = directoryRef.current;
    try {
      const discovered = await scanStudyDirectory(directory);
      const found = await mergeStudyIds(discovered, primaryStudies);
      if (directoryRef.current === directory) {
        setStudies(found);
        folderCache.save(directory, found);
        if (secondaryEnabled && secondaryDirectory && secondaryCache.allowed) {
          const secondaryDiscovered = await scanStudyDirectory(secondaryDirectory);
          const secondaryFound = await mergeStudyIds(secondaryDiscovered, secondaryStudies);
          setSecondaryStudies(secondaryFound);
          secondaryCache.save(secondaryDirectory, secondaryFound);
          return combinedStudies(found, secondaryFound);
        }
        return combinedStudies(found);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      discovery.current = false;
      setRefreshing(false);
    }
  };
  const generate = async () => {
    if (active.current || !folderCache.allowed || !selected?.ready) return;
    active.current = true;
    const token = ++operation.current;
    setError('');
    setModel(null);
    setPhase('Checking selected study for changes…');
    try {
      // Resolve only these four handles, never enumerate the directory here.
      const opened = await openStudyFiles(selected, () => token !== operation.current);
      if (token !== operation.current) return;
      let header = meta;
      const repChanged = !sameFileRevision(files?.rep, opened.rep);
      if (repChanged) {
        let parsed = selected.parsedMetadata;
        if (!parsed || !sameFileRevision(selected.repRevision, opened.rep)) {
          setPhase('Reading changed study header…');
          const buffer = await opened.rep.arrayBuffer();
          if (token !== operation.current) return;
          parsed = parseRep(buffer, selected.stem);
        }
        header = parsed.meta;
        setMeta(header);
        setWarnings(parsed.warnings);
        // Refresh the cached dropdown metadata for this study only.
        const updated = {
          ...selected,
          studyId: parsed.meta.study,
          parsedMetadata: parsed,
          repRevision: fileRevision(opened.rep),
        };
        setSelected(updated);
        const updateStudies =
          updated.studySource === 'secondary' ? setSecondaryStudies : setStudies;
        updateStudies((current) =>
          current.map((study) =>
            study.id === updated.sourceStudyKey ? { ...updated, id: study.id } : study
          )
        );
      }
      setFiles(opened);
      if (!header.start) {
        setError(
          'Recording start time could not be read. Enter the header details below, then click Generate PDF again.'
        );
        finish(token);
        return;
      }
      validateMetadata(header);
      const cached = lastBuild.current;
      if (
        cached &&
        cached.rate === rate &&
        cached.repair === repair &&
        sameQrsRevision(cached.files, opened)
      ) {
        if (repChanged)
          setMeta((current) => ({ ...current, ...initialHrBounds(cached.model.rowStats) }));
        setModel(cached.model);
        finish(token);
      } else startWorker(opened, token);
    } catch (err) {
      if (token === operation.current) {
        setError(
          err.name === 'NotFoundError'
            ? 'A selected study file was moved or deleted. Select the folder again.'
            : err.message
        );
        finish(token);
      }
    }
  };
  const field = (key, label, extra = {}) => (
    <TextField
      key={key}
      size="small"
      label={label}
      value={meta[key]}
      disabled={busy || !files}
      onChange={(event) => setMeta((current) => ({ ...current, [key]: event.target.value }))}
      inputProps={{ maxLength: key === 'facility' ? 100 : 80 }}
      {...extra}
    />
  );
  const summaryLimits = React.useMemo(() => {
    if (!model) return null;
    try {
      const rows = displayedRowStats(model.rowStats, meta).filter(
        (row) => Number.isFinite(row.min) && Number.isFinite(row.max)
      );
      if (!rows.length) return null;
      const defaults = initialHrBounds(rows);
      return {
        min: Number(meta.minFloor ?? defaults.minFloor),
        max: Number(meta.maxCeiling ?? defaults.maxCeiling),
      };
    } catch {
      return null;
    }
  }, [model, meta]);
  const prepareAlignedGraph = (limits) => {
    const updated = alignedGraphMetadata(model.rowStats, meta, limits);
    return { bytes: scenePdf(reportScene(model, updated)), commit: () => setMeta(updated) };
  };
  const filename = `${(meta.study || 'study').replace(/[^a-z0-9_-]/gi, '_')}_HR.pdf`;
  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6">HR study PDF</Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Choose a local folder, select an available study, and click Generate PDF. No files are
        uploaded.
      </Typography>
      <FolderPreset cache={folderCache} title="Primary study folder" disabled={busy} />
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Button variant="contained" disabled={busy || !folderSupported} onClick={chooseFolder}>
            {folderName ? 'Change primary study folder' : 'Select primary study folder'}
          </Button>
          {folderName && (
            <Typography component="span" sx={{ ml: 2 }}>
              {folderName}
            </Typography>
          )}
        </Box>
        <Box>
          <FormControlLabel
            label="Use secondary study folder"
            control={
              <Checkbox
                checked={secondaryEnabled}
                disabled={busy}
                onChange={handleSecondaryEnabledChange}
              />
            }
          />
          {secondaryEnabled && (
            <>
              <FolderPreset
                cache={secondaryCache}
                title="Secondary study folder (optional)"
                disabled={busy}
              />
              <Button
                variant="outlined"
                disabled={busy || !folderSupported}
                onClick={handleChooseSecondaryFolder}
              >
                {secondaryDirectory
                  ? 'Change secondary study folder'
                  : 'Select secondary study folder'}
              </Button>
            </>
          )}
        </Box>
        {!folderSupported && (
          <Alert severity="info">
            This browser or page context does not provide direct folder access. Use a browser with
            directory-picker support on HTTPS or localhost.
          </Alert>
        )}
        <Autocomplete
          options={studies}
          value={selected}
          disabled={
            busy ||
            !folderName ||
            !folderCache.allowed ||
            (secondaryEnabled && secondaryDirectory && !secondaryCache.allowed)
          }
          onOpen={refreshStudies}
          loading={refreshing}
          onChange={(event, chosen) => selectStudy(chosen)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={studyLabel}
          getOptionDisabled={(option) => !option.ready}
          filterOptions={(options, state) => filterStudies(options, state.inputValue).slice(0, 100)}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography>{studyLabel(option)}</Typography>
                <Typography variant="caption">
                  {option.studySource === 'secondary' ? 'Secondary' : 'Primary'}: {option.folder} —{' '}
                  {option.status}
                  {option.metadataWarning ? ` — ${option.metadataWarning}` : ''}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Available studies"
              placeholder="Type Study ID or filename"
              helperText={`${studies.length} studies found. Search by Study ID, filename or folder; up to 100 matches shown.`}
            />
          )}
        />
        <Typography variant="body2" color="text.secondary">
          Folder selection reads filenames and REP metadata to list Study IDs. Generate checks only
          the selected study files for changes and reuses unchanged results. Opening the study
          selector checks for added filenames in the background; existing REP metadata is reused.
        </Typography>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 2,
        }}
      >
        {field('facility', 'Customer / facility heading')}
        {field('patient', 'Patient name')}
        {field('study', 'Study ID')}
        {field('gender', 'Gender')}
        {field('age', 'Age (years)')}
        {field('start', 'Recording start date and time', {
          type: 'datetime-local',
          required: true,
          InputLabelProps: { shrink: true },
          inputProps: { step: 1 },
        })}
        <TextField
          select
          size="small"
          label="QRS sampling rate"
          value={rate}
          disabled={busy}
          onChange={(event) => {
            setRate(Number(event.target.value));
            setModel(null);
          }}
        >
          <MenuItem value={128}>128 Hz (original study)</MenuItem>
          <MenuItem value={256}>256 Hz</MenuItem>
        </TextField>
        <TextField
          size="small"
          type="number"
          label="Min Floor (BPM)"
          value={meta.minFloor ?? ''}
          disabled={busy || !model || !model.rowStats?.some((s) => s.min !== null)}
          inputProps={{ min: 0, step: 1 }}
          onChange={(event) =>
            setMeta((current) => ({
              ...current,
              stitchRowStats: undefined,
              stitchLimits: undefined,
              minFloor: event.target.value,
            }))
          }
        />
        <TextField
          size="small"
          type="number"
          label="Max Ceiling (BPM)"
          value={meta.maxCeiling ?? ''}
          disabled={busy || !model || !model.rowStats?.some((s) => s.max !== null)}
          inputProps={{ min: 0, step: 1 }}
          onChange={(event) =>
            setMeta((current) => ({
              ...current,
              stitchRowStats: undefined,
              stitchLimits: undefined,
              maxCeiling: event.target.value,
            }))
          }
        />
      </Box>
      <Typography variant="caption" color="text.secondary">
        Bounds update reported Min/Max HR immediately. Values outside the bounds are clamped; the
        closest extremes reach the floor and ceiling. Graph and Avg HR stay unchanged.
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={repair}
            disabled={busy}
            onChange={(event) => {
              setRepair(event.target.checked);
              setModel(null);
            }}
          />
        }
        label="Fill gaps using stable A/B agreement (provisional)"
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Header fields remain editable. Verify the imported information and sampling rate. Main
        annotations are preserved; unresolved intervals over 2 seconds are excluded.
      </Typography>
      <PdfReportInsert
        autoStudies={studies}
        studyDirectory={studyDirectory}
        secondaryStudyDirectory={secondaryEnabled ? secondaryDirectory : null}
        onStudiesUpdate={handleLiveStudiesUpdate}
        refreshAutoStudies={refreshStudies}
        studyAllowed={
          folderCache.allowed &&
          (!secondaryEnabled || !secondaryDirectory || secondaryCache.allowed)
        }
        autoSettings={{ rate, repair }}
        onLockChange={setInserting}
        prepareAlignedGraph={prepareAlignedGraph}
        summaryLimits={summaryLimits}
        studyId={meta.study}
        hrBytes={hrBytes}
        disabled={busy}
      />
      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          disabled={busy || !folderCache.allowed || !selected?.ready}
          onClick={generate}
        >
          Generate PDF
        </Button>
        {busy && !inserting && <Button onClick={cancel}>Cancel</Button>}
        {url && !busy && (
          <Button component="a" href={url} download={filename} variant="outlined">
            Download PDF
          </Button>
        )}
      </Stack>
      {phase && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" role="status">
            {phase}
          </Typography>
          <LinearProgress />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {warnings.join(' ')}
        </Alert>
      )}
      {model && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {model.rows} rows, {model.daysPerRow * 24} hours per row; 624 buckets per row (
          {model.bucketSeconds.toFixed(2)} seconds each). {model.added.toLocaleString()} beats
          added. {model.excluded.toLocaleString()} intervals over 2 seconds excluded. Scale: 30–
          {model.yMax} BPM; {model.clippedLow + model.clippedHigh} bucket averages outside the
          scale.
        </Alert>
      )}
      {url && !busy && (
        <Box
          component="iframe"
          src={url}
          title="HR study PDF preview"
          sx={{ mt: 2, width: '100%', height: 850, border: '1px solid', borderColor: 'divider' }}
        />
      )}
    </Paper>
  );
}
